import {
  createProduct,
  getAllProducts,
  getAttributes,
  getAttribuesByProductType,
  getCountries,
  getDataTypes,
  getOrderReferenceData,
  getProductById,
  getProductClasses,
  getProductLines,
  getProductReferenceData,
  getProductStatuses,
  getProductTypes,
  getReturnOrderReferenceData,
  getSupplierReferenceData,
  getUnits,
  MAX_PAGESIZE,
  searchProducts,
  updateProduct,
  getProductLineById,
  getDiscontinuedProducts,
  getAllManufacturers,
  getManufacturers,
  createOrUpdateSalesItem,
  getAllDiscontinuedSalesItems,
  getAllPriceLists,
  getAllSalesItems,
  getAllSalesItemsPrices,
  getCategories,
  getImagesBySku,
  getProductInfoBySku,
  getReferenceDataByProductType,
  getSalesItemAttributesBySku,
  getSalesItemById,
  getSalesItemBySku,
  getSalesItems,
  getSalesItemsByCategory,
  postPriceListBySku,
  printPriceLabel,
  removePriceListBySku
} from "src/core/api/apiRequests";
import { useGetAllPaginatedItems } from "src/core/api/useGetAllPaginatedItems";
import useResponse from "src/core/api/useResponse";
import {
  Method
} from "axios";
import useGet from "src/core/api/useGet";
import {
  useMemo
} from "react";
import type { ProductCreateRequest } from 'src/pages/products/products/submitProduct';
import type { ProductTypeResponse } from 'src/pages/products/productTypes/useProductTypesTable';
import type { PaginationResult } from 'src/core/api/useGetAllPaginatedItems';
import type { CategoryResponse } from 'src/pages/products/categories/useCategoriesTable';
import type { CreateOrUpdateSalesItemRequest } from 'src/pages/products/salesItems/submitSalesItem';
import type { ImagesResponse } from 'src/pages/products/salesItems/useSalesItemForm';
import type { SalesItemsByCategoryRequest, SalesItemsSearchCreteria } from 'src/core/api/apiRequests';
import type { KeyValuePair } from 'src/core/api/useResponse';

// Products and reference data
// GET
export const useGetProductClasses = () => {
    const { rows, setNewValue, abort } = useResponse<ProductClassResponse[]>({
        requestFunction: getProductClasses,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetProductSubClasses = (productClassId: number) => {
    const { rows, setNewValue, abort } = useResponse<IdNameDictionary[]>({
        requestFunction: getProductTypes,
        values: { productClassId },
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetProductTypes = () => {
    const { rows, setNewValue, abort } = useResponse<ProductTypeResponse[]>({
        requestFunction: getProductTypes,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetProductLines = (dependecy = null) => {
    const { rows, setNewValue, abort } = useResponse<ProductLineRepsone[]>({
        requestFunction: getProductLines,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetProductLineById = (id) => {
    const { rows: item } = useResponse<ProductLineRepsone>({
        requestFunction: getProductLineById,
        values: { id },
        requestNothingOnNull: true,
    });
    return item;
}

export const useGetProductStatuses = () => {
    const { rows, setNewValue, abort } = useResponse<IdName[]>({
        requestFunction: getProductStatuses,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetProductById = (id: string, dependecy = null) => {
    const { rows: item } = useResponse<ProductResponse>({
        requestFunction: getProductById,
        values: { id },
        requestNothingOnNull: true,
        dependency: dependecy,
    });
    return item;
}

export const useGetAllProducts = () => {
    return useGetAllPaginatedItems<ProductsResponse>({
        requestFunction: getAllProducts,
        pageSize: MAX_PAGESIZE,
    });
}

export const useGetDiscontinuedProducts = (shouldRequest: boolean) => {
    const result = useGetAllPaginatedItems<ProductsResponse>({
        requestFunction: getDiscontinuedProducts,
        pageSize: MAX_PAGESIZE,
        idField: "ProductId",
        requestValues: null,
        shouldRequest,
    });
    if (!shouldRequest) return { rows: null, status: "idle" };
    return result;
};

export const useLatestProducts = (amount = 5) => {
    const { rows: result } = useResponse<any[]>({ requestFunction: getAllProducts, values: { amount, pageSize: 1 }, requestNothingOnNull: true });
    return result;
}

export const useSearchProducts = (searchKey: string) => {
    const { rows, setNewValue, abort } = useResponse<ProductsResponse[]>({
        requestFunction: searchProducts,
        values: { searchKey },
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetCountries = () => {
    const { rows: response } = useResponse<CountryResponse[]>({
        requestFunction: getCountries,
    });
    return response;
}

export const useGetAttributes = () => {
    const { rows, setNewValue, abort } = useResponse<any[]>({
        requestFunction: getAttributes,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetAttributesByProductType = (productTypeId: string) => {
    const { rows: attributes } = useResponse<ProductTypeAttributes>({
        requestFunction: getAttribuesByProductType,
        values: { productTypeId },
        requestNothingOnNull: true,
        dependency: productTypeId,
    });
    return attributes?.Properties;
}

export const useGetUnits = () => {
    const { rows, setNewValue, abort } = useResponse<any[]>({
        requestFunction: getUnits,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetDataTypes = () => {
    const { rows: result } = useResponse<any[]>({ requestFunction: getDataTypes });
    // console.log("DataTypes", result);
    return result ?? [];
}

export const useGetProductReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getProductReferenceData,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetOrderReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getOrderReferenceData,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetSupplierReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getSupplierReferenceData,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}

export const useGetReturnOrderReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getReturnOrderReferenceData,
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}


// POST
export const useCreateProduct = (productCreateRequest: ProductCreateRequest) => {
    const { rows, setNewValue, abort } = useResponse<ProductResponse>({
        requestFunction: createProduct,
        values: { productCreateRequest },
    });
    return [rows, setNewValue, abort] as [typeof rows, typeof setNewValue, typeof abort];
}
// PUT
export const useUpdateProduct = (id: number, productUpdateRequest: ProductCreateRequest) => {
    const { rows: response, setNewValue: setResponse, abort, loading, status } = 
    useResponse({ requestFunction: updateProduct, values: { id, productUpdateRequest }, dependency: id,  logLevel:["error"] });
    return [response, setResponse, abort, loading, status];
}

// Manufacturers
export const useManufacturers = (dependency = null) => {
    const {rows:data, status} = useResponse<any[]>({requestFunction:getManufacturers,values:null, requestNothingOnNull:false, dependency});
    return {data, status};
}

// export const useManufacturers = (dependecy = null) => {
//     const response = useGetAllPaginatedItems<any>(getAllManufacturers, 500,["Manufacturer","Key"]);
//     return response;
// }

// Legacy Manufacturer data-only contract
export const useManufacturersLegacy = (dependecy = null) => {
    const { rows: response } = useResponse<any>({
        requestFunction: getManufacturers,
        values: null,
        requestNothingOnNull: false,
        dependency: dependecy,
    });
    return response;
}

// Sales Items, categories and pricing
export const useGetSalesItemById = (id: number): SalesItemModel => {
    const { rows: salesItem } = useResponse<SalesItemModel>({
        requestFunction: getSalesItemById,
        values: { id },
        requestNothingOnNull: true,
    });
    return salesItem;
}

export const useGetSalesItemBySku = (sku: string, dependency =null): SalesItemModel => {
    const { rows: salesItem } = useResponse<SalesItemModel>({
        requestFunction: getSalesItemBySku,
        values: { sku },
        requestNothingOnNull: true,
        dependency,
    });
    return salesItem;
}

export const useGetSalesItems = (sc: SalesItemsSearchCreteria) => {
    const { rows: salesItems } = useResponse<PaginationResult<SalesItemListItem>>({
        requestFunction: getSalesItems,
        values: sc,
    });
    return salesItems;
}


export const useGetSalesItemsByCategory = (si: SalesItemsByCategoryRequest): PaginationResult<SalesItemListItem> => {
    const { rows: salesItems } = useResponse<PaginationResult<SalesItemListItem>>({
        requestFunction: getSalesItemsByCategory,
        values: si,
    });
    return salesItems;
}




export const useGetAllSalesItems = (shouldRequest= true) => {
    return useGetAllPaginatedItems<SalesItemListItem[]>({requestFunction:getAllSalesItems, pageSize:500, idField:"ArticleNumber",  shouldRequest});
}

export const useGetSalesItemPrices = (priceListId?: number | string | null) => {
    const requestValues = useMemo(
        () => (priceListId ? { priceListId } : null),
        [priceListId]
    );

    return useGetAllPaginatedItems<SalesItemListItem>({
        requestFunction: getAllSalesItemsPrices,
        pageSize: 500,
        idField: "SalesItemId",
        requestValues,
        shouldRequest: !!priceListId,
    });
};

export const useGetAllDiscontinuedSalesItems = (shouldRequest: boolean) => {
    const result = useGetAllPaginatedItems<SalesItemListItem[]>({
        requestFunction: getAllDiscontinuedSalesItems,
        pageSize: 500,
        idField: "ArticleNumber",
        requestValues: null,
        shouldRequest,
    });
    if (!shouldRequest) return { rows: null, status: "idle" };
    return result;
}


export const useLatestSalesItems = (amount = 5) => {
    const {rows: result} = useResponse<any[]>({requestFunction:getAllSalesItems,values:{amount,pageSize:1}, requestNothingOnNull:true});
    return result;
}

export const usePrintPriceLabel = (salesItemId: string, priceListId: number) => {
    const {rows: result} = useResponse<Blob>({requestFunction:printPriceLabel,values:{salesItemId, priceListId}, requestNothingOnNull:true, responseType:"blob"});
    return result;
}


export const useCreateOrUpdateSalesItem = (salesItem: CreateOrUpdateSalesItemRequest, method: Method = "POST", id: number = null) => {
    const request = createOrUpdateSalesItem(salesItem, method, id);
    const result = useGet<SalesItemModel>(request);
    return result;
}

export const useProductInfo = (sku: string, languageId?: number, dependecy?) => {
    const { rows: productInfo } = useResponse<any>({
        requestFunction: getProductInfoBySku,
        values: { sku, languageId },
        requestNothingOnNull: true,
        dependency: [languageId, dependecy],
    });
    return productInfo;
}

export const useGetSalesItemAttributes = (sku: string) => {
    const { rows: salesItemAttributes } = useResponse<any>({
        requestFunction: getSalesItemAttributesBySku,
        values: { sku },
        requestNothingOnNull: true,
    });
    return salesItemAttributes;
}

export const useGetSalesItemAttributeReferenceData = (productTypeId: number) => {
    const { rows: referenceData } = useResponse<any>({
        requestFunction: getReferenceDataByProductType,
        values: { productTypeId },
        requestNothingOnNull: true,
        dependency: productTypeId,
    });
    return referenceData;
}

export const useProductImages = (sku: string, dependency = null) => {
    const { rows: images } = useResponse<ImagesResponse[]>({
        requestFunction: getImagesBySku,
        values: { sku },
        requestNothingOnNull: true,
        dependency,
    });
    return images;
}

export const useGetSalesAssortmentsWithPriceLists = () =>{
       const response = useResponse<any[]>({requestFunction:getAllPriceLists});
       return response;
}

export const useUpdatePriceListBySku = (priceListPost) => {
    const sku = priceListPost?.sku;
    const newPriceLists = priceListPost?.priceList;
    const request = postPriceListBySku(sku, newPriceLists);
    const {results, abort, status} = useGet(request, newPriceLists );
    return results;
}

export const usRemovePriceListBySku = (priceListPost) => {
    const sku = priceListPost?.sku;
    const removePriceLists = priceListPost?.priceList;
    const request = removePriceListBySku(sku, removePriceLists);
    const{results, abort, status} = useGet(request, removePriceLists );
    return results;
}


export const useGetCategories = (inputLanguage?,dependency = null) => {
    const { rows: categories } = useResponse<CategoryResponse[]>({
        requestFunction: getCategories,
        values: { inputLanguage },
        requestNothingOnNull: false,
        dependency,
    });
    return categories;
}

interface IdNameLocalized {
  Id: number;
  Name: [string, string];
}

interface CountryResponse {
  Id?: number;
  Iso3166Code?: string;
  Iso3166Name?: string;
  IsEUMember?: boolean;
  CountryCallingCode?: string;
}

interface ProductResponse {
  ProductId?: string;
  Name?: string;
  ManufacturerProductId?: string;
  ManufacturerAltProductId?: string;
  ManufacturerProductName?: string;
  Manufacturer?: KeyValuePair;
  ProductType?: KeyValuePair;
  ProductLine?: KeyValuePair;
  ProductClass?: KeyValuePair;
  ProductSubClass?: KeyValuePair;
  TARIC?: string;
  CountryOfOrigin?: KeyValuePair;
  IsPhysical?: boolean;
  IsDigital?: boolean;
  Status?: KeyValuePair;
  Created?: Date;
  Modified?: Date;
  ValidFrom?: Date;
  ValidTo?: Date;
  SupplierProducts?: any;
  SalesItems?: any;
  isEditPage?: string;
}

interface ProductsResponse {
  ProductId?: string;
  Name?: string;
  ProductTypesId?: number;
  ProductType?: KeyValuePair;
  InformationRequired?: string;
  ProductLine?: KeyValuePair;
  Manufacturer?: KeyValuePair;
  ManufacturerProductId?: string;
  ManufacturerProductName?: string;
  ValidFromDate?: Date;
  ValidToDate?: Date;
  ProductClass?: KeyValuePair;
  ProductSubClass?: KeyValuePair;
  ProductStatus?: KeyValuePair;
  ProductStatusId?: number;
  RequiredProperties?: number;
  RequiredPropertiesEntered?: number;
  ManufacturerPartnerId?: number;
  ProductLineId?: number;
  VariantCount?: number;
  IsPhysical?: boolean;
  IsDigital?: boolean;
  IsCustomerSpecific?: boolean;
  TARIC?: number | string;
  CountryOfOriginId?: number;
  CountryOfOrigin?: KeyValuePair;
}

interface IdNameDictionary {
  Id: number;
  Name: IdNameLocalized;
}

interface ProductSubClass {
  ProductClassId?: Number;
  ProductSubClass?: KeyValuePair;
}

interface ProductClassResponse extends KeyValuePair {
  ProductSubClasses?: ProductSubClass[];
}

interface ProductLineRepsone {
  ProductLine: KeyValuePair;
}

interface SalesItemListItem {
  SalesItemId: number;
  ArticleNumber: string;
  Name: string;
  ManufacturerProductId: string;
  ManufacturerName: string;
  ProductType: KeyValuePair;
  SalesItemType: KeyValuePair;
  ProductClass: string;
  ProductSubClass: string;
  ProductLine: string;
  OptionGroup: string;
  Status: KeyValuePair;
  AvailableQty?: number;
  ValidFrom?: Date;
  ValidTo?: Date;
  Created: Date;
  Modified: Date;
  Thumbnail?: string;
}

interface SalesItemModel {
  SalesItemId: number;
  ArticleNumber: string;
  Name: string;
  ManufacturerProductId: string;
  ManufacturerName: string;
  ProductType: KeyValuePair;
  Status: KeyValuePair;
  SalesItemType: KeyValuePair;
  ProductClass: string;
  ProductSubClass: string;
  ProductLine: string;
  OptionGroup: string;
  HasCustomizedItems?: boolean;
  AvailableQty?: number;
  ValidFrom?: Date;
  ValidTo?: Date;
  Created?: Date;
  Modified?: Date;
  Products?: SalesItemProductsModel[];
  SalesAssortments?: SalesItemAssortmentsModel[];
  Categories?: SalesItemCategoriesModel[];
  'Image[0].DisplayName'?: string;
  'Image[0].FileName'?: string;
  Image0DisplayName?: string;
  Test?: any;
  ImageValues: any[];
}

interface SalesItemProductsModel {
  ProductsId: string;
  ProductName: string;
  ManufacturerProductId: string;
  ManufacturerName: string;
  Quantity: number;
  UnitQuantity?: number;
  MainItem: boolean;
  ProductClass: string;
  ProductSubClass: string;
  ProductType: KeyValuePair;
  ProductValidFromDate?: Date;
  ProductValidToDate?: Date;
  ProductStatus: KeyValuePair;
  SalesItemsId: number;
  ArticleNumber: string;
  SalesItemName: string;
  SalesItemStatus: number;
  SalesItemValidFromDate?: Date;
  SalesItemValidToDate?: Date;
  Thumbnail?: string;
}

interface SalesItemAssortmentsModel {
  SalesAssortment: KeyValuePair;
  SalesAssortmentItemsId?: number;
  SalesItemsId: number;
  ArticleNumber: string;
  SalesItemName: string;
  SalesPriceList: KeyValuePair;
  Organisation: KeyValuePair;
  AdjustedCost?: number;
  SupplyCost?: number;
  PriceExcTax?: number;
  PriceIncTax?: number;
  TaxRate?: number;
  Margin?: number;
  Profit?: number;
  Currency: KeyValuePair;
  CampaignSalesAssortmentItemsId?: number;
  Campaign: KeyValuePair;
  CampaignPrice?: number;
}

interface SalesItemCategoriesModel {
  GroupTree: KeyValuePair;
  GroupTreeNode: KeyValuePair;
  NodePath: string;
}

interface Property {
  Name?: string;
  Id?: number;
  Enabled?: boolean;
  Required?: boolean;
  Description?: string;
  BaseProductTypeId?: number;
}

interface ProductTypeAttributes {
  Attribute?: KeyValuePair;
  Properties?: Property[] | any[];
  Id?: number;
}

interface IdName {
  Id?: number;
  Name?: string;
}
